import { Immutable } from "immer";
import React from "react";

import type {
    GoogleTagManagerBlock,
    OneTrustCookieSettingsBlock,
    MetaTagsBlock as Tags,
} from "@reactivated";

import { MetaTagsBlock } from "@thelabnyc/thelabui/src/components/MetaTagsBlock/index";

import {
    getGtmBaseUrl,
    getGtmScriptUrlPrefix,
    gtmNoscriptIframe,
    gtmScript,
} from "../../utils/gtm";
import { usePageSetting } from "../../utils/settings";

const RenderNothing = (): React.ReactNode => null;

/**
 * GTM bootstrap, server-rendered against the site's own tagging host.
 *
 * Replaces thelabui's default, which loads from www.googletagmanager.com from
 * an effect. Most TSI brands proxy GTM through a first-party host, and the tag
 * stack must survive a hydration failure.
 */
const GoogleTagManager = (
    props: Immutable<{ id: string; value: GoogleTagManagerBlock }>,
): React.ReactNode => {
    const fqdn = usePageSetting("gtm_fqdn");
    const path = usePageSetting("gtm_path");
    const gatewayPath = usePageSetting("gtm_gateway_path");
    const containerId = props.value.container_id;
    if (!containerId) {
        return null;
    }
    const baseUrl = getGtmBaseUrl(fqdn ?? "", path ?? "");
    return (
        <>
            <noscript
                dangerouslySetInnerHTML={{
                    __html: gtmNoscriptIframe(baseUrl, containerId),
                }}
            />
            <script
                type="text/javascript"
                dangerouslySetInnerHTML={{
                    __html: gtmScript(
                        getGtmScriptUrlPrefix(baseUrl, gatewayPath ?? ""),
                        containerId,
                    ),
                }}
            />
        </>
    );
};

/**
 * OneTrust consent loader. React 19 hoists it to <head>.
 *
 * Replaces thelabui's default, which appends the tag from an effect: consent
 * must load even when hydration does not.
 */
const OneTrustCookieSettings = (
    props: Immutable<{ id: string; value: OneTrustCookieSettingsBlock }>,
): React.ReactNode => {
    const domainScript = props.value.domain_script_uuid;
    if (!domainScript) {
        return null;
    }
    return (
        <script
            async={true}
            charSet="UTF-8"
            data-domain-script={domainScript}
            src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
            type="text/javascript"
        />
    );
};

// Built once, not per render: a fresh object would remount every child.
const DEPENDENCIES = {
    full: {
        GoogleTagManagerBlock: GoogleTagManager,
        OneTrustCookieSettingsBlock: OneTrustCookieSettings,
    },
    noOneTrust: {
        GoogleTagManagerBlock: GoogleTagManager,
        OneTrustCookieSettingsBlock: RenderNothing,
    },
    noGtm: {
        GoogleTagManagerBlock: RenderNothing,
        OneTrustCookieSettingsBlock: OneTrustCookieSettings,
    },
    neither: {
        GoogleTagManagerBlock: RenderNothing,
        OneTrustCookieSettingsBlock: RenderNothing,
    },
} as const;

type IProps = Immutable<{
    tags: Tags;
    doNotTrack: boolean;
    showOneTrust?: boolean;
}>;

/**
 * Third-party tags authored under Wagtail's Settings > Meta Tag Settings.
 *
 * Render at the top of <body>: React 19 hoists the <meta> and async <script
 * src> children to <head> from there, while an inline <script> in a
 * React-owned <head> risks a hydration mismatch when a third party injects a
 * script of its own ahead of it.
 */
export const MetaTags = (props: IProps): React.JSX.Element => {
    const dependencies = props.doNotTrack
        ? props.showOneTrust === false
            ? DEPENDENCIES.neither
            : DEPENDENCIES.noGtm
        : props.showOneTrust === false
          ? DEPENDENCIES.noOneTrust
          : DEPENDENCIES.full;
    return <MetaTagsBlock tags={[...props.tags]} dependencies={dependencies} />;
};
